import { useState, useCallback } from 'react';
import { RefreshCw, Upload, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useToolUsage } from '@/hooks/useToolUsage';

const FORMATS = [
  { value: 'image/png', label: 'PNG', ext: 'png' },
  { value: 'image/jpeg', label: 'JPG', ext: 'jpg' },
  { value: 'image/webp', label: 'WebP', ext: 'webp' },
];

const ImageConverter = () => {
  useToolUsage('image-converter');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [format, setFormat] = useState('image/png');
  const [quality, setQuality] = useState([90]);

  const handleUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f || !f.type.startsWith('image/')) { toast.error('Please upload an image'); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }, []);

  const isLossy = format === 'image/jpeg' || format === 'image/webp';
  const selectedFormat = FORMATS.find(f => f.value === format)!;

  const handleDownload = () => {
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(blob => {
        if (!blob) return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${file.name.replace(/\.[^/.]+$/, '')}.${selectedFormat.ext}`;
        a.click();
        toast.success(`Converted to ${selectedFormat.label}!`);
      }, format, isLossy ? quality[0] / 100 : undefined);
    };
    img.src = preview;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <RefreshCw className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Image Format Converter</h1>
          <p className="text-muted-foreground">Convert images between PNG, JPG, and WebP</p>
        </div>
      </div>

      {!file ? (
        <Card>
          <CardContent className="pt-6">
            <label className="block cursor-pointer">
              <div className="border-2 border-dashed border-border rounded-xl p-12 text-center hover:border-primary transition-colors">
                <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                <p className="font-medium">Drop an image here or click to upload</p>
              </div>
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
            </label>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="pt-6">
              <div className="aspect-video bg-muted rounded-lg overflow-hidden flex items-center justify-center">
                <img src={preview} alt="Preview" className="max-w-full max-h-full object-contain" />
              </div>
              <p className="text-sm text-muted-foreground mt-2 text-center">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 space-y-5">
              <div>
                <Label className="text-sm mb-2 block">Output Format</Label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FORMATS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {isLossy && (
                <div>
                  <div className="flex justify-between mb-2">
                    <Label className="text-sm">Quality</Label>
                    <span className="text-sm font-bold text-primary">{quality[0]}%</span>
                  </div>
                  <Slider value={quality} onValueChange={setQuality} min={10} max={100} step={5} />
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button onClick={handleDownload} className="flex-1">
                  <Download className="h-4 w-4 mr-2" /> Convert & Download
                </Button>
                <Button variant="outline" onClick={() => { setFile(null); setPreview(''); }}>Clear</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ImageConverter;
